/**
 * Persistence for "Your Turn" answers.
 *
 * The `useLocalAnswer` hook is the surface every lesson page consumes. Its
 * signature is intentionally unchanged from the localStorage-only preview:
 * lessons do not need to know whether they are running in the static preview
 * or against a real logged-in student.
 *
 * Behavior:
 *
 * - Static preview (`VITE_STATIC_PREVIEW=1`): pure localStorage, no fetch.
 *   This is what the deployed `/computer/a` preview runs on.
 *
 * - Production (student is signed in): localStorage as an optimistic cache,
 *   Turso via `/api/answers/:lesson/:promptSlug` as the source of truth.
 *   Server wins on conflict (newer `updatedAt`), so a student who edits on
 *   another device will see that edit propagate on next mount.
 *
 * The `Saved` indicator reflects the local cache in both environments. When
 * the server round-trip fails (offline, session expired), the answer is still
 * in localStorage and will be retried on next mount. The one-time migration
 * (`importLocalAnswersToServer`) sweeps up anything left behind the first
 * time a student authenticates.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from './api';

const STORAGE_PREFIX = 'constare:answer:';
const IMPORT_MARKER = 'constare:answer-import:done';

const IS_STATIC_PREVIEW = import.meta.env.VITE_STATIC_PREVIEW === '1';

type SaveStatus = 'idle' | 'saving' | 'saved';

export function answerKey(lesson: string, slug: string): string {
  return `${STORAGE_PREFIX}${lesson}:${slug}`;
}

function readLocal(lesson: string, slug: string): string {
  if (typeof window === 'undefined') return '';
  try {
    return window.localStorage.getItem(answerKey(lesson, slug)) ?? '';
  } catch {
    return '';
  }
}

function writeLocal(lesson: string, slug: string, value: string): void {
  if (typeof window === 'undefined') return;
  try {
    if (value.trim().length === 0) {
      window.localStorage.removeItem(answerKey(lesson, slug));
    } else {
      window.localStorage.setItem(answerKey(lesson, slug), value);
    }
  } catch {
    // Storage can fail (private mode, quota). Silently continue; the server
    // still holds the truth once persist() succeeds.
  }
}

type AnswerResponse = { body: string; updatedAt: string | null };

/**
 * Synchronous local read. Used by callback panels (e.g. Lesson 8.5 Week 1
 * calibration) that just need whatever is cached at render time. In
 * production, `useAnswerHydration` (below) will have already reconciled the
 * cache with the server before these panels render.
 */
export function readAnswer(lesson: string, slug: string): string {
  return readLocal(lesson, slug);
}

/**
 * Two-way bound answer with autosave + a status indicator the UI can surface.
 *
 * - Text stays in local React state for responsive typing.
 * - On blur (and on a 600ms idle debounce), the value is written to the local
 *   cache immediately (so "Saved" flips fast) and then to the server in the
 *   background.
 * - On mount, we return the cached value synchronously and then kick off a
 *   server fetch that reconciles: if the server has a newer answer, we swap
 *   in the server value; otherwise we keep the local one.
 */
export function useLocalAnswer(lesson: string, slug: string) {
  const [value, setValue] = useState<string>(() => readLocal(lesson, slug));
  const [status, setStatus] = useState<SaveStatus>(() =>
    readLocal(lesson, slug) ? 'saved' : 'idle'
  );
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latestValueRef = useRef<string>(value);
  latestValueRef.current = value;

  // On mount: reconcile with server if we are in a real (non-preview) build.
  useEffect(() => {
    if (IS_STATIC_PREVIEW) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get<AnswerResponse>(
          `/api/answers/${encodeURIComponent(lesson)}/${encodeURIComponent(slug)}`
        );
        if (cancelled) return;
        // Server wins only if the server has content and the user has not
        // started typing something new locally in the interim. Prevents a
        // slow server response from clobbering an in-progress edit.
        if (res.body && res.body !== latestValueRef.current) {
          if (latestValueRef.current.trim().length === 0) {
            writeLocal(lesson, slug, res.body);
            setValue(res.body);
            setStatus('saved');
          }
        }
      } catch {
        // Unauthenticated, offline, or server down. The local cache is still
        // authoritative for this session and will retry on next mount.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [lesson, slug]);

  const persist = useCallback(
    async (next: string) => {
      writeLocal(lesson, slug, next);
      setStatus('saved');
      if (IS_STATIC_PREVIEW) return;
      try {
        await api.put(
          `/api/answers/${encodeURIComponent(lesson)}/${encodeURIComponent(slug)}`,
          { body: next }
        );
      } catch {
        // Local cache still has it. Migration import (or the next successful
        // save) will get it upstream.
      }
    },
    [lesson, slug]
  );

  const onChange = useCallback(
    (next: string) => {
      setValue(next);
      setStatus('saving');
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        void persist(next);
      }, 600);
    },
    [persist]
  );

  const flush = useCallback(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    void persist(latestValueRef.current);
  }, [persist]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  return { value, onChange, onBlur: flush, status };
}

/**
 * One-time migration. Called after a student authenticates: sweeps every
 * `constare:answer:*` key out of localStorage into `/api/answers/import`,
 * then marks the browser as imported so we do not re-run. Safe to call
 * multiple times: the server only inserts rows that do not already exist.
 *
 * Call this from `App.tsx` (or wherever the "user is signed in" transition
 * lives) inside a `useEffect` gated on `me.user`.
 */
export async function importLocalAnswersToServer(): Promise<{
  imported: number;
  skipped: number;
} | null> {
  if (IS_STATIC_PREVIEW) return null;
  if (typeof window === 'undefined') return null;
  try {
    if (window.localStorage.getItem(IMPORT_MARKER) === '1') return null;
  } catch {
    return null;
  }

  const entries: { lesson: string; promptSlug: string; body: string }[] = [];
  try {
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const key = window.localStorage.key(i);
      if (!key || !key.startsWith(STORAGE_PREFIX)) continue;
      const rest = key.slice(STORAGE_PREFIX.length);
      const colon = rest.indexOf(':');
      if (colon <= 0) continue;
      const lesson = rest.slice(0, colon);
      const promptSlug = rest.slice(colon + 1);
      const body = window.localStorage.getItem(key) ?? '';
      if (!body) continue;
      entries.push({ lesson, promptSlug, body });
    }
  } catch {
    return null;
  }

  if (entries.length === 0) {
    try {
      window.localStorage.setItem(IMPORT_MARKER, '1');
    } catch {
      // ignore
    }
    return { imported: 0, skipped: 0 };
  }

  try {
    const res = await api.post<{ imported: number; skipped: number }>(
      '/api/answers/import',
      { entries }
    );
    try {
      window.localStorage.setItem(IMPORT_MARKER, '1');
    } catch {
      // ignore
    }
    return { imported: res.imported ?? 0, skipped: res.skipped ?? 0 };
  } catch {
    // Not signed in or server unreachable. Do NOT set the marker; try again
    // on next mount.
    return null;
  }
}

/** Stable slugs for the Lesson 1.1 calibration prompts. */
export const LESSON_1_1_PROMPTS = {
  STUDENT_AB: 'student-a-or-b',
  SKIPPED: 'skipped-thinking',
  DID_THINKING: 'did-thinking',
} as const;

export const LESSON_1_1_PROMPT_LABELS: Record<string, string> = {
  [LESSON_1_1_PROMPTS.STUDENT_AB]:
    'Of Student A and Student B, which one are you closer to right now, honestly?',
  [LESSON_1_1_PROMPTS.SKIPPED]:
    'One specific way you used AI in the last week where you skipped the thinking part.',
  [LESSON_1_1_PROMPTS.DID_THINKING]:
    'One specific way you used AI in the last week where you did the thinking first.',
};
